import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoArrowBackOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import "../css/mainhome.css";
import "../css/LegalPages.css";

function PageHeader({ title }) {
  const navigate = useNavigate();

  return (
    <div className="legal-header">
      <button className="legal-back-btn" onClick={() => navigate(-1)} aria-label="Go back">
        <IoArrowBackOutline size={24} />
      </button>
      <h2>{title}</h2>
    </div>
  );
}

function LegalContentPage({ title, subtitle, introLines, sections }) {
  const renderLine = (line) => {
    const parts = line.split(/(www\.gama567app\.net|support@gama567app\.net)/g);

    return parts.map((part, index) => {
      if (part === "www.gama567app.net") {
        return (
          <a key={`${part}-${index}`} href="https://www.gama567app.net" target="_blank" rel="noopener noreferrer">
            {part}
          </a>
        );
      }

      if (part === "support@gama567app.net") {
        return (
          <a key={`${part}-${index}`} href="mailto:support@gama567app.net">
            {part}
          </a>
        );
      }

      return part;
    });
  };

  return (
    <div className="mainhome-screen-wrapper">
      <PageHeader title={title} />
      <main className="legal-content">
        {subtitle && <p className="legal-subtitle">{subtitle}</p>}
        {introLines && (
          <div className="legal-intro">
            {introLines.map((line) => (
              <p className={line.className} key={line.text}>{renderLine(line.text)}</p>
            ))}
          </div>
        )}
        {sections.map((section) => (
          <section className="legal-section" key={section.title}>
            <h3>{section.title}</h3>
            {section.body.map((line) => (
              <p key={line}>{renderLine(line)}</p>
            ))}
          </section>
        ))}
      </main>
    </div>
  );
}

export function PrivacyPolicy() {
  const privacyIntroLines = [
    {
      text: "Effective Date: 7 July 2026",
      className: "legal-intro-date",
    },
    {
      text: "Welcome to Gama567 Shop. Your privacy is important to us. This Privacy Policy explains how we collect, use, store, and protect your information when you use our application.",
      className: "legal-intro-welcome",
    },
  ];

  const inactivePrivacySections = [
    {
      title: "1. Information We Collect",
      body: [
        "When you register or use our app, we may collect:",
        "Full Name",
        "Mobile Phone Number",
        "Password (stored securely)",
        "Order History",
      ],
    },
    {
      title: "2. Registration & Login",
      body: [
        "To create an account, you need to provide: Name, Mobile Number, and Password. For security purposes, we send a 4-digit One-Time Password (OTP) to your registered mobile number. After successful OTP verification, your account is created. To log in, simply enter your Registered Mobile Number and Password.",
      ],
    },
    {
      title: "3. Wallet & Add Funds",
      body: [
        "Our app provides an Add Fund feature that allows users to add money to their wallet for a faster and hassle-free checkout experience while purchasing products. Wallet balance can only be used for purchases within the app.",
      ],
    },
    {
      title: "4. Orders & Refund Policy",
      body: [
        "Users can purchase products using their wallet balance. If an order is cancelled according to our cancellation policy, the eligible amount will be refunded to the user's wallet. Refund processing time may vary depending on verification.",
      ],
    },
    {
      title: "5. Order Modification",
      body: [
        "If you wish to modify or update an order after placing it, please contact our Customer Support via WhatsApp with your: Registered Name, Registered Mobile Number, and Order Details. Our support team will assist you whenever possible.",
      ],
    },
    {
      title: "6. Product Information",
      body: [
        "All products available on Gama567 Shop are listed for shopping and personal use. Product details, images, prices, and descriptions are provided to help users make informed purchase decisions.",
      ],
    },
    {
      title: "7. Copyright",
      body: [
        "All product images, designs, text, graphics, logos, and other content available through the app are protected by copyright. Copyright Gama567 Shop. All Rights Reserved. Unauthorized copying, reproduction, redistribution, resale, or modification of any product content without written permission is strictly prohibited.",
      ],
    },
    {
      title: "8. Data Security",
      body: [
        "We take reasonable security measures to protect your personal information from unauthorized access, misuse, alteration, or disclosure.",
      ],
    },
    {
      title: "9. Sharing of Information",
      body: [
        "We do not sell, rent, or trade your personal information to third parties. Information may only be shared:",
        "To comply with legal requirements.",
        "To process payments and wallet transactions through authorized payment partners.",
        "To protect the security and integrity of our services.",
      ],
    },
    {
      title: "10. User Responsibilities",
      body: [
        "Users are responsible for:",
        "Keeping their login credentials secure.",
        "Maintaining the confidentiality of their password.",
        "Ensuring the accuracy of the information they provide.",
      ],
    },
    {
      title: "11. Changes to This Privacy Policy",
      body: [
        "We may update this Privacy Policy from time to time. Any changes will be posted within the application and become effective immediately upon publication.",
      ],
    },
    {
      title: "12. Contact Us",
      body: [
        "For any questions regarding this Privacy Policy, order modifications, refunds, or other support, please contact us through our Customer Support WhatsApp available in the app.",
      ],
    },
    {
      title: "Disclaimer",
      body: [
        "Gama567 Shop provides product listings, descriptions, images, pricing, wallet, order, and refund support for shopping purposes. Product information is provided to help users understand available items before placing an order. Users are responsible for reviewing product details and using purchased products appropriately. The shop and the app shall not be liable for any loss or damage resulting from misuse of products or incorrect information provided by the user.",
        "Copyright © 2026 Gama567 Shop. All Rights Reserved.",
      ],
    },
  ];

  return <LegalContentPage title="Privacy Policy" introLines={privacyIntroLines} sections={inactivePrivacySections} />;
}

export function TermsConditions() {
  const termsIntroLines = [
    {
      text: "Effective Date: 07 July 2026",
      className: "legal-intro-date",
    },
    {
      text: "Welcome to Gama567 Shop. By registering, accessing, or using our application, you agree to the following Terms & Conditions. If you do not agree with these terms, please do not use the application.",
      className: "legal-intro-welcome",
    },
  ];

  const inactiveTermsSections = [
    {
      title: "1. Acceptance of Terms",
      body: [
        "By creating an account or using our services, you confirm that you have read, understood, and agreed to these Terms & Conditions and our Privacy Policy.",
      ],
    },
    {
      title: "2. User Registration",
      body: [
        "To use our services, you must register by providing:",
        "Full Name",
        "Mobile Number",
        "Password",
        "A 4-digit One-Time Password (OTP) will be sent to your registered mobile number for verification. Your account will be activated only after successful OTP verification.",
      ],
    },
    {
      title: "3. Login",
      body: [
        "You are responsible for maintaining the confidentiality of your login credentials. To log in, you must enter: Registered Mobile Number and Password. You are responsible for all activities performed through your account.",
      ],
    },
    {
      title: "4. Wallet & Add Fund",
      body: [
        "Our application provides an Add Fund feature that allows users to add money to their wallet for a smooth and hassle-free checkout experience. Wallet balance can only be used within the Gama567 Shop application.",
        "Users are responsible for verifying the amount before adding funds.",
        "Wallet balances cannot be transferred to another account unless permitted by applicable law or our policies.",
      ],
    },
    {
      title: "5. Product Purchases",
      body: [
        "Users may purchase products using the available wallet balance. After successful payment:",
        "The order will be processed.",
        "Order details will be available in your account.",
        "Users should verify all order details before confirming the purchase.",
      ],
    },
    {
      title: "6. Cancellation & Refund",
      body: [
        "Orders may be cancelled according to our cancellation policy. If a cancellation is approved, the eligible refund will be credited to the user's wallet. Refund processing time may vary depending on verification.",
      ],
    },
    {
      title: "7. Order Modification",
      body: [
        "If you wish to modify your order after placing it, please contact our Customer Support via WhatsApp with: Registered Name, Registered Mobile Number, and Order Details. Our team will make reasonable efforts to assist you where possible.",
      ],
    },
    {
      title: "8. Product Information",
      body: [
        "All products available on Gama567 Shop are listed for shopping and personal use. Product details, prices, images, and descriptions are provided to help users make informed purchase decisions. Users are responsible for reviewing product details before placing an order.",
      ],
    },
    {
      title: "9. Intellectual Property",
      body: [
        "All product images, designs, logos, graphics, text, and application content are the exclusive property of Gama567 Shop. Copyright Gama567 Shop. All Rights Reserved. Users may not: Copy, Reproduce, Modify, Distribute, Resell, Publish, or Share any content without prior written permission.",
      ],
    },
    {
      title: "10. User Conduct",
      body: [
        "Users agree not to:",
        "Provide false information.",
        "Attempt unauthorized access to the application.",
        "Misuse wallet services.",
        "Copy or distribute copyrighted product images, designs, or app content.",
        "Engage in any illegal activity while using the application.",
      ],
    },
    {
      title: "11. Privacy",
      body: [
        "Your personal information is collected and processed according to our Privacy Policy. We take reasonable measures to protect your information and do not sell or rent your personal data to third parties.",
      ],
    },
    {
      title: "12. Limitation of Liability",
      body: [
        "Gama567 Shop shall not be responsible for:",
        "Any direct or indirect loss resulting from misuse of purchased products.",
        "Any interruption of services.",
        "Any unauthorized access caused by the user's negligence.",
        "Any incorrect order details or information provided by users.",
      ],
    },
    {
      title: "13. Changes to Terms",
      body: [
        "We reserve the right to modify these Terms & Conditions at any time. Updated terms will become effective immediately after being published within the application.",
      ],
    },
    {
      title: "14. Contact Support",
      body: [
        "For assistance regarding Orders, Wallet, Refunds, Order Modification, or Account Issues, please contact our Customer Support WhatsApp available within the application.",
      ],
    },
    {
      title: "15. Governing Law",
      body: [
        "These Terms & Conditions shall be governed and interpreted in accordance with the applicable laws of India.",
      ],
    },
    {
      title: "Disclaimer",
      body: [
        "All products available on Gama567 Shop are provided for shopping and personal use. Product information is provided to help users understand available items before placing an order. Users are responsible for reviewing product details, order information, and using purchased products appropriately.",
        "Copyright © 2026 Gama567 Shop. All Rights Reserved.",
      ],
    },
  ];

  return (
    <LegalContentPage
      title="Terms & Conditions"
      introLines={termsIntroLines}
      sections={inactiveTermsSections}
    />
  );
}

export function DeleteAccountPolicy() {
  const [mobileNumber, setMobileNumber] = useState("");
  const [password, setPassword] = useState("");

  const handleSubmit = (event) => {
    event.preventDefault();
    const normalizedMobile = mobileNumber.trim();

    if (!/^[6-9]\d{9}$/.test(normalizedMobile)) {
      toast.error("Please enter a valid 10 digit mobile number.");
      return;
    }

    toast.success("Your delete account request has been received.");
    setMobileNumber("");
    setPassword("");
  };

  return (
    <div className="mainhome-screen-wrapper legal-delete-wrapper">
      <PageHeader title="Delete Account" />
      <main className="delete-account-content">
        <form className="delete-account-card" onSubmit={handleSubmit}>
          <h2>Delete Your Account</h2>
          <input
            type="tel"
            placeholder="Mobile Number"
            value={mobileNumber}
            onChange={(event) => setMobileNumber(event.target.value.replace(/\D/g, "").slice(0, 10))}
            maxLength={10}
            inputMode="numeric"
            pattern="[6-9][0-9]{9}"
            required
          />
          <input
            type="password"
            placeholder="Password"

            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
          <button type="submit">Delete Account</button>
        </form>
      </main>
    </div>
  );
}
